
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import crossbreedingOrchestrator from '../services/crossbreeding-service/crossbreeding-orchestrator';
import SimulationResults from './SimulationResults.vue';
import Options from './Options.vue';
import GeneInputs from './GeneInputs.vue';
import SaplingScreenCapture from './SaplingScreenCapture.vue';
import {
  GeneticsMap,
  CrossbreedingOrchestratorEventListenerCallbackData,
  GeneticsMapGroup,
  SimulatorEventType
} from '@/services/crossbreeding-service/models';
import Sapling from '@/models/sapling.model';
import goTo from 'vuetify/lib/services/goto';
import SimulationMapGroup from './SimulationMapGroup.vue';
import SimulationMapGroupBrowser from './SimulationMapGroupBrowser.vue';
import ApplicationOptions from '@/interfaces/application-options';
import { timeMsToTimeString } from '@/lib/time-utils';
import HighlightedMap from './HighlightedMap.vue';

@Component({
  components: {
    SimulationResults,
    Options,
    GeneInputs,
    SaplingScreenCapture,
    SimulationMapGroup,
    SimulationMapGroupBrowser,
    HighlightedMap
  }
})
export default class CrossbreedingSimulator extends Vue {
  @Prop({ type: String }) readonly selectedPlantTypeName: string;
  progressPercents: number[] = [];
  /** Avoid rewriting document.title on every worker tick. */
  private lastTitlePercentEmitted = -1;
  isSimulating = false;
  showResultsPanel = false;
  lastEstimatedTimeUpdateTimestamp = new Date().getTime();
  numberOfGenerations = 0;
  currentGenerationIndex = 1;
  areInputsValid = false;
  calcStartTime: number | null = null;
  calcEndTime: number | null = null;
  options: ApplicationOptions | null = null;
  highlightedMap: GeneticsMap | null = null;
  selectedBrowsingGroup: GeneticsMapGroup | null = null;
  isSelectedBrowsingGroupFromHighlight = false;
  selectedBrowsingGroup2: GeneticsMapGroup | null = null;
  resultMapGroups: ReadonlyArray<GeneticsMapGroup> | null = null;
  sourceGeneList: string[] = [];
  isScreenScanning = false;
  appVersion = process.env.VUE_APP_VERSION;
  /** Align right filters with left lead-text row. */
  resultsFiltersOffsetPx = 0;
  private resultsAlignResizeBound: (() => void) | null = null;
  get showHighlight() {
    return this.highlightedMap !== null;
  }

  /** Hide scan control during multi-gen runs unless user is actively scanning (matches prior behavior). */
  get scanToolbarSlotHidden() {
    return this.isSimulating && this.numberOfGenerations > 1 && !this.isScreenScanning;
  }

  get showSkipInToolbar() {
    return this.isSimulating && this.numberOfGenerations > 1;
  }

  /** When scan slot is hidden, put Skip between Stop and Settings (centered in the row). */
  get toolbarSkipBeforeOptions() {
    return this.showSkipInToolbar && this.scanToolbarSlotHidden;
  }

  get toolbarRowColsClass() {
    if (this.showSkipInToolbar && this.scanToolbarSlotHidden) {
      return 'studio-page__toolbar-row--cols-3';
    }
    if (this.showSkipInToolbar && !this.scanToolbarSlotHidden) {
      return 'studio-page__toolbar-row--cols-4';
    }
    return 'studio-page__toolbar-row--cols-3';
  }

  get calcTotalTime() {
    if (this.calcStartTime && this.calcEndTime) {
      const timeDiff = this.calcEndTime - this.calcStartTime;
      return timeMsToTimeString(timeDiff);
    }
    return null;
  }

  @Watch('calcTotalTime')
  onCalcTotalTimeChanged(val: string | null) {
    this.$emit('total-time-updated', val);
  }

  get shouldDisplayGenInfoOnMapBrowser() {
    return (
      this.isSelectedBrowsingGroupFromHighlight &&
      this.selectedBrowsingGroup &&
      !this.selectedBrowsingGroup2 &&
      this.selectedBrowsingGroup.mapList[0].resultSapling.generationIndex > 1
    );
  }

  /** 2 vs 3 for browser helper copy (only gen 2–3 are used in practice). */
  get mapBrowserGenerationIndex(): number {
    const g = this.selectedBrowsingGroup;
    if (!g || !g.mapList.length) return 2;
    return g.mapList[0].resultSapling.generationIndex;
  }

  get shouldDisplayGeoInfoOnMapBrowser() {
    const shouldGroupDisplayGeoInfo = (grp: GeneticsMapGroup | null) =>
      grp && (grp.mapList[0]?.chance < 1 || grp.mapList[1]?.chance < 1 || grp.mapList[2]?.chance < 1);
    return (
      shouldGroupDisplayGeoInfo(this.selectedBrowsingGroup) || shouldGroupDisplayGeoInfo(this.selectedBrowsingGroup2)
    );
  }

  get isCalculatingLastGeneration() {
    return this.isSimulating && this.currentGenerationIndex === this.options?.numberOfGenerations;
  }

  get hasProgressStartedOnCurrentGeneration() {
    return this.progressPercents[this.currentGenerationIndex - 1] !== undefined;
  }

  constructor() {
    super();
    crossbreedingOrchestrator.addEventListener(this.onCrossbreedingServiceEvent);
  }

  mounted() {
    this.options = (this.$refs.options as Options).getOptions();
    this.resultsAlignResizeBound = () => this.syncResultsFiltersOffset();
    window.addEventListener('resize', this.resultsAlignResizeBound, { passive: true });
    this.$nextTick(() => this.syncResultsFiltersOffset());
  }

  beforeDestroy() {
    if (this.resultsAlignResizeBound) {
      window.removeEventListener('resize', this.resultsAlignResizeBound);
      this.resultsAlignResizeBound = null;
    }
  }

  @Watch('showResultsPanel')
  onShowResultsPanelChanged(v: boolean) {
    if (v) {
      this.$nextTick(() => this.syncResultsFiltersOffset());
    } else {
      this.resultsFiltersOffsetPx = 0;
    }
  }

  @Watch('$i18n.locale')
  onI18nLocaleChanged() {
    this.$nextTick(() => this.syncResultsFiltersOffset());
  }

  private syncResultsFiltersOffset() {
    if (typeof window !== 'undefined' && window.innerWidth < 1040) {
      this.resultsFiltersOffsetPx = 0;
      return;
    }
    const plannerLead = this.$refs.plannerLead as HTMLElement | undefined;
    const resultsPanel = this.$refs.results as HTMLElement | undefined;
    const filtersForm = resultsPanel?.querySelector?.('.results-filters-form') as HTMLElement | null;
    if (!plannerLead || !filtersForm) {
      this.resultsFiltersOffsetPx = 0;
      return;
    }
    const leadTop = plannerLead.getBoundingClientRect().top;
    const filtersTop = filtersForm.getBoundingClientRect().top;
    this.resultsFiltersOffsetPx = Math.round(leadTop - filtersTop);
  }

  onCrossbreedingServiceEvent(type: string, data: CrossbreedingOrchestratorEventListenerCallbackData) {
    if (type === SimulatorEventType.PROGRESS_UPDATE) {
      this.setProgress(data.generationIndex, data.progressPercent || 0);
      if (data.estimatedTimeMs) {
        this.updateEstimatedTime(data.estimatedTimeMs);
      }
    } else if (type === SimulatorEventType.PARTIAL_RESULTS || type === SimulatorEventType.DONE_GENERATION) {
      const hadNoResultsBefore = this.resultMapGroups === null;
      if (hadNoResultsBefore) {
        this.showResultsPanel = true;
        this.scrollToResults();
      }
      if (type === SimulatorEventType.PARTIAL_RESULTS) {
        this.setData(data.mapGroups as GeneticsMapGroup[], false);
      }
      if (type === SimulatorEventType.DONE_GENERATION) {
        this.setData(data.mapGroups as GeneticsMapGroup[], data.generationIndex === this.numberOfGenerations);
        this.setProgress(data.generationIndex, 100);
        this.currentGenerationIndex += 1;
        this.updateEstimatedTime(null);
      }
    } else if (type === SimulatorEventType.DONE) {
      this.calcEndTime = Date.now();
      this.lastTitlePercentEmitted = -1;
      this.updateEstimatedTime(null);
      this.updateTitle();
      this.isSimulating = false;
    }
  }

  setData(mapGroups: GeneticsMapGroup[], isFinalResult: boolean) {
    // For displaying partial results cloning is required to prevent Vue from constantly observing the results.
    this.resultMapGroups = Object.freeze(isFinalResult ? mapGroups : mapGroups.map((mapGroup) => mapGroup.clone()));
    this.$nextTick(() => this.syncResultsFiltersOffset());
  }

  setProgress(generationIndex: number, progressPercent: number) {
    const generationProgressIndex = generationIndex - 1;
    // To decrease the number of updates on the UI we only update on change of the most significant decimal place.
    const progressPercentWithOneDecimal = Number(progressPercent.toFixed(1));
    if (this.progressPercents[generationProgressIndex] !== progressPercentWithOneDecimal) {
      this.onNextTickRerender(() => {
        Vue.set(this.progressPercents, generationProgressIndex, progressPercentWithOneDecimal);
      });
    }
    const progressPercentWithNoDecimals = Math.round(progressPercent || 0);
    if (progressPercentWithNoDecimals !== this.lastTitlePercentEmitted) {
      this.lastTitlePercentEmitted = progressPercentWithNoDecimals;
      this.updateTitle(generationIndex, progressPercentWithNoDecimals);
    }
  }

  updateTitle(generationIndex?: number | undefined, progressPercent?: number | undefined) {
    // Background tab: skip title thrash (browsers still parse updates); always run when resetting title after completion.
    if (typeof document !== 'undefined' && document.hidden && generationIndex !== undefined) {
      return;
    }
    if (
      (progressPercent === 100 && generationIndex === this.options?.numberOfGenerations) ||
      generationIndex === undefined
    ) {
      document.title = String(this.$t('app.titleBreeder'));
    } else {
      document.title = String(this.$t('app.titleProgress', { pct: progressPercent, gen: generationIndex }));
    }
  }

  updateEstimatedTime(estimatedTime: number | null) {
    if (typeof document !== 'undefined' && document.hidden && estimatedTime != null) {
      return;
    }
    const currentTimestamp = new Date().getTime();
    if (!estimatedTime || currentTimestamp - this.lastEstimatedTimeUpdateTimestamp > 250) {
      this.$emit('estimated-time-updated', estimatedTime);
      this.lastEstimatedTimeUpdateTimestamp = currentTimestamp;
    }
  }

  handleSimulateClick() {
    if (this.options) {
      this.clearHighlight();
      const deduplicatedSaplingGeneList = (this.$refs.geneInputs as GeneInputs).getDeduplicatedSaplingGeneList();
      this.sourceGeneList = [...deduplicatedSaplingGeneList];
      (this.$refs.simulationResults as SimulationResults)?.resetPage();
      this.numberOfGenerations = this.options.numberOfGenerations;
      this.progressPercents = new Array(this.options.numberOfGenerations);
      this.currentGenerationIndex = 1;
      this.resultMapGroups = null;
      this.calcStartTime = Date.now();
      this.calcEndTime = null;
      this.lastTitlePercentEmitted = -1;
      crossbreedingOrchestrator.simulateBestGenetics(
        deduplicatedSaplingGeneList.map((geneString, index) => new Sapling(geneString, 0, index)),
        undefined,
        this.options
      );
      this.isSimulating = true;

      if (this.options.autoSaveInputSets) {
        (this.$refs.geneInputs as GeneInputs).storeCurrentSet();
      }
    }
  }

  handleSkipClick() {
    crossbreedingOrchestrator.skipToNextGeneration();
  }

  handleStopSimulationClick() {
    this.isSimulating = false;
    this.lastTitlePercentEmitted = -1;
    this.updateEstimatedTime(null);
    this.updateTitle();
    crossbreedingOrchestrator.cancelSimulation();
  }

  handleSaplingScannedEvent(value: string) {
    const geneInputs = this.$refs.geneInputs as GeneInputs | undefined;
    if (geneInputs) {
      geneInputs.handleSaplingScannedEvent(value);
    }
  }

  handleInputsValidityChange(valid: boolean) {
    this.areInputsValid = valid;
  }

  handleClearHighlightClick() {
    this.clearHighlight();
  }

  clearHighlight() {
    this.highlightedMap = null;
  }

  handleMapSelectedEvent(map: GeneticsMap) {
    this.highlightedMap = map;
    this.selectedBrowsingGroup = null;
    this.onNextTickRerender(() => {
      const rect = (this.$refs.highlightedMap as HTMLElement)?.getBoundingClientRect();
      const topDistance = rect.top + rect.height / 2 + window.scrollY - window.innerHeight / 2;
      goTo(topDistance, { duration: 200 });
    });
  }

  handleGroupSelectedEvent(group: GeneticsMapGroup) {
    this.isSelectedBrowsingGroupFromHighlight = false;
    this.selectedBrowsingGroup = group;
  }

  handleHighlightComposingSaplingSelectedEvent(group: GeneticsMapGroup) {
    this.isSelectedBrowsingGroupFromHighlight = true;
    this.selectedBrowsingGroup = group;
  }

  handleBrowsingGroupComposingSaplingSelected(group: GeneticsMapGroup) {
    this.selectedBrowsingGroup2 = group;
  }

  handleBrowserLeaveEvent() {
    if (this.selectedBrowsingGroup2) {
      this.selectedBrowsingGroup2 = null;
    } else {
      this.selectedBrowsingGroup = null;
    }
  }

  handleOptionsSetEvent(options: ApplicationOptions) {
    this.options = options;
  }

  async handleShareRequested() {
    const geneInputs = this.$refs.geneInputs as GeneInputs;
    const genes = geneInputs.getDeduplicatedSaplingGeneList();

    const url = new URL(window.location.origin + window.location.pathname);
    if (genes.length > 0) {
      url.searchParams.set('genes', genes.join(','));
    }

    const results = this.$refs.simulationResults as SimulationResults | undefined;
    if (results) {
      const { geneCount, filteringGenes } = results;
      if (geneCount.gCount) url.searchParams.set('gCount', String(geneCount.gCount));
      if (geneCount.yCount) url.searchParams.set('yCount', String(geneCount.yCount));
      if (geneCount.hCount) url.searchParams.set('hCount', String(geneCount.hCount));
      for (let i = 0; i < 6; i++) {
        const v = filteringGenes[`gene${i}`];
        if (v) url.searchParams.set(`g${i}`, v);
      }
    }

    try {
      await navigator.clipboard.writeText(url.toString());
    } catch {
      const ta = document.createElement('textarea');
      ta.value = url.toString();
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    geneInputs.showShareConfirmation();
  }

  scrollToResults() {
    setTimeout(() => {
      const rect = (this.$refs.results as HTMLElement)?.getBoundingClientRect();
      goTo(rect.top - 64, { duration: 300 });
    }, 200);
  }
}
